import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { normalizeHoumaoWorkspaceSearch } from '../lib/houmao-workspace';
import {
  fetchAgentProfiles,
  type RuntimeAgentProfile,
} from '../lib/agent-profiles-api';
import {
  buildHoumaoWebChatUrl,
  resolveHoumaoDirectExpertProfileId,
} from '../lib/houmao-web-chat-entry';

export default function HoumaoExpertRedirectPage() {
  const { expertSlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [profiles, setProfiles] = useState<RuntimeAgentProfile[] | null>(null);

  const resolvedProfileId = useMemo(
    () => resolveHoumaoDirectExpertProfileId(expertSlug, profiles ?? []),
    [expertSlug, profiles],
  );

  useEffect(() => {
    if (resolvedProfileId || profiles !== null) {
      return;
    }

    let cancelled = false;

    void fetchAgentProfiles('web')
      .then((payload) => {
        if (!cancelled) {
          setProfiles(payload.profiles);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfiles([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [profiles, resolvedProfileId]);

  if (resolvedProfileId) {
    return <Navigate to={{ pathname: '/chat', search: buildRedirectChatSearch(resolvedProfileId, searchParams), hash: location.hash }} state={location.state} replace />;
  }

  if (profiles !== null) {
    return <Navigate to={{ pathname: '/', search: location.search, hash: location.hash }} state={location.state} replace />;
  }

  return <div>Opening expert session...</div>;
}

function buildRedirectChatSearch(profileId: string, original: URLSearchParams): string {
  const params = new URLSearchParams(buildHoumaoWebChatUrl(profileId).split('?')[1]);
  original.forEach((value, key) => {
    // The short path selects the expert; other navigation context is retained.
    if (key !== 'agent_profile') params.set(key, value);
  });
  return normalizeHoumaoWorkspaceSearch(`?${params.toString()}`);
}
